import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Tooltip,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Search,
  Refresh,
  Visibility,
  Edit,
  Receipt,
  ShoppingCart,
  LocalShipping,
  CheckCircle,
  Cancel,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { useBackofficeAuth } from '../../hooks/useBackofficeAuth';
import { getBackofficeAuthHeaders } from '../../services/api';
import { API_BASE_URL } from '../../config/constants';
import { useBackofficeInvoice } from '../../hooks/useBackofficeInvoice';
import SyncStatusCell from '../../components/backoffice/SyncStatusCell';
import PlantAssignDialog from '../../components/backoffice/PlantAssignDialog';
import BackofficeDmsDocumentsDialog from '../../components/backoffice/BackofficeDmsDocumentsDialog';
import DmsDocumentsPanel from '../../components/DmsDocumentsPanel';
import { backofficeManagementService } from '../../services/backofficeManagementService';
import { DmsDocumentResponse } from '../../services/invoiceService';

interface Order {
  id: string;
  order_number: string;
  customer_id: string;
  customer_name: string;
  customer_email?: string;
  status: string;
  total_amount: number;
  original_total_amount?: number;
  total_discount_amount?: number;
  currency: string;
  order_date: string;
  delivery_date?: string;
  shipping_date?: string;
  invoice_date?: string;
  item_count: number;
  created_at: string;
  updated_at: string;
  epicor_sync_status?: string | null;
  epicor_order_num?: number | string | null;
  last_sync_attempt_at?: string | null;
  last_sync_success_at?: string | null;
  sync_error_message?: string | null;
  plant_code?: string | null;
  // Origin of the order. 'portal' for portal-placed orders, 'epicor'
  // for offline (sales-rep placed) orders imported from Epicor. Drives
  // the Source column + filter and the sales-rep block in the detail dialog.
  created_via?: 'portal' | 'epicor' | null;
  sales_rep_code?: string | null;
  sales_rep_name?: string | null;
  // Resolved rep from the workforce cache — name/email/role. Present
  // on both list and detail responses when the code was found.
  sales_rep?: {
    code: string;
    name: string | null;
    email: string | null;
    role_code: string | null;
    role_description: string | null;
    is_active: boolean;
  } | null;
}

interface OrderItem {
  id: string;
  product_id: string;
  product_name: string;
  product_code: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  discount_percent?: number;
  discount_amount?: number;
}

interface Customer {
  id: string;
  company_name: string;
  contact_person?: string;
  email?: string;
}

// Shape returned by POST /backoffice/orders/{id}/resync-with-epicor.
// Direction distinguishes "pull latest status from Epicor" from
// "push draft order forward to Epicor" so the toast can label
// correctly.
interface ResyncResult {
  order_id: string;
  order_number: string;
  direction: 'status' | 'forward';
  success: boolean;
  changes: string[];
  error: string | null;
  synced_at: string | null;
}

interface OrderDetailDialogProps {
  order: Order | null;
  open: boolean;
  onClose: () => void;
  // Optional so callers that don't want the "Sync with Epicor"
  // button can omit the prop. When supplied, drives the button
  // visibility check + click handler + inline spinner state.
  onResync?: (order: Order) => void;
  resyncingOrderId?: string | null;
  canResyncOrder?: (order: Order) => boolean;
}

const OrderDetailDialog: React.FC<OrderDetailDialogProps> = ({
  order,
  open,
  onClose,
  onResync,
  resyncingOrderId,
  canResyncOrder,
}) => {
  const { t } = useTranslation();
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(false);

  // Embedded DMS documents state -- only fetched when the order is
  // INVOICED or CLOSED (Zegen only produces docs for posted
  // invoices). Kept as local state (not lifted) because the DMS
  // section is self-contained; the parent OrderManagementPage's
  // standalone dialog reads via its own hook, unrelated to this
  // embedded view.
  const [dmsDocuments, setDmsDocuments] = useState<DmsDocumentResponse | null>(null);
  const [dmsLoading, setDmsLoading] = useState(false);
  const [dmsError, setDmsError] = useState<string | null>(null);

  useEffect(() => {
    if (order && open) {
      loadOrderDetails();
    }
  }, [order, open]);

  // Load DMS documents when the dialog opens on a posted-invoice
  // order. Runs after loadOrderDetails since both are triggered by
  // the same open/order effect deps but do independent work.
  useEffect(() => {
    if (!order || !open) return;
    const canHaveDmsDocs = ['INVOICED', 'CLOSED'].includes(order.status);
    if (!canHaveDmsDocs) {
      setDmsDocuments(null);
      return;
    }
    const loadDms = async (forceRefresh: boolean = false) => {
      setDmsLoading(true);
      setDmsError(null);
      try {
        const inv = await backofficeManagementService.getOrderInvoice(order.id);
        if (!inv.success || !inv.invoice_id) {
          setDmsDocuments(null);
          setDmsError(inv.message || null);
          return;
        }
        const docs = await backofficeManagementService.getInvoiceDmsDocuments(
          inv.invoice_id,
          forceRefresh,
        );
        setDmsDocuments(docs);
      } catch (err) {
        setDmsError(err instanceof Error ? err.message : 'Failed to load DMS documents');
        setDmsDocuments(null);
      } finally {
        setDmsLoading(false);
      }
    };
    loadDms(false);
    // Expose to onRefresh via closure below (not returned from effect).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id, order?.status, open]);

  // Handlers wired into <DmsDocumentsPanel>. Refresh calls the load
  // function again with force=true; download proxies to the backoffice
  // service. Kept as separate memoised functions so the panel doesn't
  // re-render on every parent render.
  const handleDmsRefresh = useCallback(async () => {
    if (!order || !open) return;
    setDmsLoading(true);
    setDmsError(null);
    try {
      const inv = await backofficeManagementService.getOrderInvoice(order.id);
      if (!inv.success || !inv.invoice_id) {
        setDmsDocuments(null);
        setDmsError(inv.message || 'No invoice found');
        return;
      }
      const docs = await backofficeManagementService.getInvoiceDmsDocuments(
        inv.invoice_id,
        true,
      );
      setDmsDocuments(docs);
    } catch (err) {
      setDmsError(err instanceof Error ? err.message : 'Failed to refresh DMS documents');
    } finally {
      setDmsLoading(false);
    }
  }, [order, open]);

  const handleDmsDownload = useCallback(
    async (
      docType: 'signed-invoice' | 'delivery-note' | 'faktur-pajak',
      docIndex?: number,
    ) => {
      if (!order) return;
      try {
        const inv = await backofficeManagementService.getOrderInvoice(order.id);
        if (!inv.success || !inv.invoice_id) return;
        await backofficeManagementService.downloadDmsDocument(
          inv.invoice_id,
          docType,
          docIndex ?? 0,
        );
      } catch (err) {
        setDmsError(err instanceof Error ? err.message : 'Failed to download document');
      }
    },
    [order],
  );

  const loadOrderDetails = async () => {
    if (!order) return;

    setLoading(true);
    try {
      // Load order items
      const itemsResponse = await fetch(`${API_BASE_URL}/backoffice/orders/${order.id}/items`, {
        headers: getBackofficeAuthHeaders(),
      });
      
      if (itemsResponse.ok) {
        const itemsData = await itemsResponse.json();
        setOrderItems(itemsData.items || []);
      }

      // Load customer details
      const customerResponse = await fetch(`${API_BASE_URL}/backoffice/customers/${order.customer_id}`, {
        headers: getBackofficeAuthHeaders(),
      });
      
      if (customerResponse.ok) {
        const customerData = await customerResponse.json();
        setCustomer(customerData);
      }
    } catch (error) {
      console.error('Error loading order details:', error);
    } finally {
      setLoading(false);
    }
  };


  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"> = {
      'DRAFT': 'default',
      'SUBMITTED': 'primary',
      'CONFIRMED': 'info',
      'SHIPPED': 'warning',
      'DELIVERED': 'success',
      'CANCELLED': 'error',
      'INVOICED': 'secondary',
      'CLOSED': 'success'
    };
    return statusColors[status] || 'default';
  };

  if (!order) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <ShoppingCart />
          Order Details - {order.order_number}
          <Chip
            label={order.status}
            color={getStatusColor(order.status)}
            size="small"
          />
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {/* Order Summary */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Typography variant="h6">Order Summary</Typography>
                  {order.created_via === 'epicor' ? (
                    <Chip label="Offline (Epicor)" color="default" variant="outlined" size="small" />
                  ) : (
                    <Chip label="Online (Portal)" color="primary" variant="outlined" size="small" />
                  )}
                </Box>
                <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
                  <Box>
                    <Typography><strong>Order Number:</strong> {order.order_number}</Typography>
                    <Typography><strong>Customer:</strong> {order.customer_name}</Typography>
                    <Typography><strong>Email:</strong> {order.customer_email || 'N/A'}</Typography>
                    <Typography><strong>Order Date:</strong> {new Date(order.order_date).toLocaleDateString()}</Typography>
                    {order.delivery_date && (
                      <Typography><strong>Delivery Date:</strong> {new Date(order.delivery_date).toLocaleDateString()}</Typography>
                    )}
                    {(order.sales_rep_name || order.sales_rep?.name) && (
                      <Typography>
                        <strong>Sales Rep:</strong> {order.sales_rep?.name || order.sales_rep_name}
                        {(order.sales_rep?.code || order.sales_rep_code) && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({order.sales_rep?.code || order.sales_rep_code})
                          </Typography>
                        )}
                        {order.sales_rep?.email && (
                          <>
                            {' — '}
                            <a href={`mailto:${order.sales_rep.email}`}>
                              {order.sales_rep.email}
                            </a>
                          </>
                        )}
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography><strong>Total Amount:</strong> {formatCurrency(order.total_amount)}</Typography>
                    {order.original_total_amount && order.total_discount_amount && (
                      <>
                        <Typography><strong>Original Amount:</strong> {formatCurrency(order.original_total_amount)}</Typography>
                        <Typography><strong>Discount Applied:</strong> {formatCurrency(order.total_discount_amount)}</Typography>
                      </>
                    )}
                    <Typography><strong>Items Count:</strong> {order.item_count}</Typography>
                    <Typography><strong>Status:</strong> <Chip label={order.status} color={getStatusColor(order.status)} size="small" /></Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Order Items */}
            <Typography variant="h6" gutterBottom>Order Items</Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product Code</TableCell>
                    <TableCell>Product Name</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Discount</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.product_code}</TableCell>
                      <TableCell>{item.product_name}</TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                      <TableCell align="right">
                        {item.discount_percent ? `${item.discount_percent}%` : 
                         item.discount_amount ? formatCurrency(item.discount_amount) : '-'}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(item.total_price)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* DMS Documents (embedded section). Only shown for
                INVOICED / CLOSED orders since Zegen only produces
                docs after Epicor Finance posts the legal invoice.
                Uses the shared <DmsDocumentsPanel> so the layout
                matches the customer flow + the standalone
                BackofficeDmsDocumentsDialog. */}
            {order && ['INVOICED', 'CLOSED'].includes(order.status) && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  {dmsError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {dmsError}
                    </Alert>
                  )}
                  <DmsDocumentsPanel
                    dmsDocuments={dmsDocuments}
                    isLoading={dmsLoading}
                    onRefresh={handleDmsRefresh}
                    onDownload={handleDmsDownload}
                  />
                </CardContent>
              </Card>
            )}

            {/* Status Information */}
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Order Status</Typography>
                <Alert severity="info">
                  Order status is managed by Epicor ERP and cannot be updated from the backoffice.
                </Alert>
              </CardContent>
            </Card>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {/* Render Sync button only when the parent supplied the
            handler AND the specific order is resync-eligible.
            Keeps the dialog reusable in contexts where sync isn't
            wired up. */}
        {onResync && canResyncOrder && order && canResyncOrder(order) && (
          <Button
            variant="outlined"
            color="primary"
            startIcon={
              resyncingOrderId === order.id ? (
                <CircularProgress size={16} />
              ) : (
                <SyncIcon />
              )
            }
            onClick={() => onResync(order)}
            disabled={resyncingOrderId === order.id}
          >
            Sync with Epicor
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const OrderManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useBackofficeAuth();
  // Only the alerts consume the hook now. Invoice download went
  // through the useBackofficeInvoice hook when the Receipt icon did
  // a direct fetch; the icon now opens the DMS Documents dialog
  // instead, so the download-adjacent destructures are gone.
  const {
    error: invoiceError,
    infoMessage: invoiceInfoMessage,
  } = useBackofficeInvoice();

  // State for orders list
  const [orders, setOrders] = useState<Order[]>([]);
  const [plantDialog, setPlantDialog] = useState<{ open: boolean; order: Order | null }>({
    open: false,
    order: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  
  // Dialog state
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // "Sync with Epicor" resync state. resyncingOrderId is the id of
  // the row currently in-flight (drives the per-row spinner);
  // resyncResult holds the returned changes/error for the outcome
  // dialog. resyncError holds transport-level failures we can't
  // shape into a ResyncResult (network drop, 404, 409, 502).
  const [resyncingOrderId, setResyncingOrderId] = useState<string | null>(null);
  const [resyncResult, setResyncResult] = useState<ResyncResult | null>(null);
  const [resyncError, setResyncError] = useState<string | null>(null);

  // DMS Documents dialog state. dmsDialogOrder is the order whose
  // DMS documents are currently being viewed; null when the dialog
  // is closed.
  const [dmsDialogOrder, setDmsDialogOrder] = useState<Order | null>(null);

  // Load orders with filters
  const loadOrders = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        size: rowsPerPage.toString(),
      });
      
      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      if (dateFilter !== 'all') {
        params.append('date_filter', dateFilter);
      }

      if (sourceFilter !== 'all') {
        params.append('source', sourceFilter);
      }

      const response = await fetch(`${API_BASE_URL}/backoffice/orders?${params}`, {
        headers: {
          ...getBackofficeAuthHeaders(),
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load orders: ${response.statusText}`);
      }

      const data = await response.json();
      setOrders(data.items || []);
      setTotalCount(data.total || 0);
    } catch (err) {
      console.error('Error loading orders:', err);
      setError(err instanceof Error ? err.message : 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, page, rowsPerPage, searchTerm, statusFilter, dateFilter, sourceFilter]);

  // Initial load
  useEffect(() => {
    if (isAuthenticated) {
      loadOrders();
    }
  }, [loadOrders, isAuthenticated]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 0) {
        loadOrders();
      } else {
        setPage(0);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, dateFilter, sourceFilter]);

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setDetailDialogOpen(true);
  };


  // handleDownloadInvoice removed on 2026-07-18: consolidated with the
  // "DMS Documents" dialog. The Receipt icon on each row now opens
  // the dialog (BackofficeDmsDocumentsDialog) which surfaces the
  // signed invoice + delivery notes + faktur pajak in one place.

  // Manual "Sync with Epicor". Runs the same reconcile the hourly
  // cron does, but for one order right now. Direction is decided
  // server-side:
  //   * has epicor_order_num -> pull status from Epicor -> portal
  //   * DRAFT + no epicor_order_num -> push order forward to Epicor
  // Any other state (e.g. SUBMITTED with no epicor_order_num) is
  // rejected by the API with 409 -- we surface that as an error toast
  // rather than guessing what to do.
  const handleResyncWithEpicor = async (order: Order) => {
    setResyncError(null);
    setResyncingOrderId(order.id);
    try {
      const response = await fetch(
        `${API_BASE_URL}/backoffice/orders/${order.id}/resync-with-epicor`,
        {
          method: 'POST',
          headers: getBackofficeAuthHeaders(),
        },
      );

      if (response.ok) {
        const result: ResyncResult = await response.json();
        setResyncResult(result);
        // Reflect the moved fields in the row without a full reload
        // so the operator sees the change immediately. loadOrders()
        // would repaint everything and lose scroll position on long
        // pages.
        if (result.success && result.changes.length > 0) {
          loadOrders();
        }
      } else {
        // API returned a shaped error we should surface verbatim:
        //   404 -> order not found
        //   409 -> ambiguous state (OrderResyncNotEligible)
        //   502 -> Epicor call failed
        let message = `Sync failed (HTTP ${response.status})`;
        try {
          const body = await response.json();
          if (typeof body?.detail === 'string') {
            message = body.detail;
          } else if (body?.detail?.message) {
            message = `${body.detail.message}: ${body.detail.error || 'no detail'}`;
          }
        } catch {
          // Body wasn't JSON; keep the fallback message.
        }
        setResyncError(message);
      }
    } catch (err) {
      // Network drop, CORS, etc.
      setResyncError(err instanceof Error ? err.message : 'Network error while syncing');
    } finally {
      setResyncingOrderId(null);
    }
  };

  // A resync is only meaningful in two states: the order is already
  // in Epicor (pull status) or is a DRAFT waiting to be pushed
  // forward. Anything else (SUBMITTED without an epicor_order_num,
  // cancelled with no Epicor row, etc.) the backend will 409, so
  // hide the button entirely for those rows to avoid clicks that
  // always fail.
  const canResyncOrder = (order: Order): boolean => {
    if (order.epicor_order_num !== null && order.epicor_order_num !== undefined) {
      return true;
    }
    return order.status === 'DRAFT';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusChip = (status: string) => {
    const statusColors: Record<string, "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"> = {
      'DRAFT': 'default',
      'SUBMITTED': 'primary',
      'CONFIRMED': 'info',
      'SHIPPED': 'warning',
      'DELIVERED': 'success',
      'CANCELLED': 'error',
      'INVOICED': 'secondary',
      'CLOSED': 'success'
    };

    const statusIcons: Record<string, React.ReactElement> = {
      'DRAFT': <Edit sx={{ fontSize: 16 }} />,
      'SUBMITTED': <ShoppingCart sx={{ fontSize: 16 }} />,
      'CONFIRMED': <CheckCircle sx={{ fontSize: 16 }} />,
      'SHIPPED': <LocalShipping sx={{ fontSize: 16 }} />,
      'DELIVERED': <CheckCircle sx={{ fontSize: 16 }} />,
      'CANCELLED': <Cancel sx={{ fontSize: 16 }} />,
      'INVOICED': <Receipt sx={{ fontSize: 16 }} />,
      'CLOSED': <CheckCircle sx={{ fontSize: 16 }} />
    };

    return (
      <Chip
        label={status}
        color={statusColors[status] || 'default'}
        size="small"
        icon={statusIcons[status]}
      />
    );
  };

  const isInvoiceAvailable = (status: string): boolean => {
    return status === 'INVOICED' || status === 'CLOSED';
  };

  if (!isAuthenticated) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Login required to access order management</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Order Management
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Box sx={{ flexGrow: 1, minWidth: 300 }}>
            <TextField
              fullWidth
              label="Search Orders"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by order number, customer name, or email"
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
              }}
            />
          </Box>
          
          <Box sx={{ minWidth: 150 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="DRAFT">Draft</MenuItem>
                <MenuItem value="SUBMITTED">Submitted</MenuItem>
                <MenuItem value="CONFIRMED">Confirmed</MenuItem>
                <MenuItem value="SHIPPED">Shipped</MenuItem>
                <MenuItem value="DELIVERED">Delivered</MenuItem>
                <MenuItem value="INVOICED">Invoiced</MenuItem>
                <MenuItem value="CLOSED">Closed</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ minWidth: 150 }}>
            <FormControl fullWidth>
              <InputLabel>Date</InputLabel>
              <Select
                value={dateFilter}
                label="Date"
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
                <MenuItem value="quarter">This Quarter</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ minWidth: 150 }}>
            <FormControl fullWidth>
              <InputLabel>Source</InputLabel>
              <Select
                value={sourceFilter}
                label="Source"
                onChange={(e) => setSourceFilter(e.target.value)}
              >
                <MenuItem value="all">All Sources</MenuItem>
                <MenuItem value="portal">Online (Portal)</MenuItem>
                <MenuItem value="epicor">Offline (Epicor)</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ minWidth: 120 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={loadOrders}
              disabled={loading}
              fullWidth
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Invoice error message */}
      {invoiceError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Invoice Error: {invoiceError}
        </Alert>
      )}
      
      {/* Invoice info message */}
      {invoiceInfoMessage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {invoiceInfoMessage}
        </Alert>
      )}

      {/* Orders table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order #</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Epicor Sync</TableCell>
                <TableCell>Plant</TableCell>
                <TableCell>Items</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Order Date</TableCell>
                <TableCell>Delivery Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    No orders found
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {order.order_number}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {order.id.substring(0, 8)}...
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      {order.created_via === 'epicor' ? (
                        <Box>
                          <Chip
                            label="Offline"
                            color="default"
                            variant="outlined"
                            size="small"
                          />
                          {order.sales_rep_name && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {order.sales_rep_name}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Chip
                          label="Online"
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      )}
                    </TableCell>

                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {order.customer_name}
                        </Typography>
                        {order.customer_email && (
                          <Typography variant="caption" color="text.secondary">
                            {order.customer_email}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      {getStatusChip(order.status)}
                    </TableCell>

                    <TableCell>
                      <SyncStatusCell
                        status={order.epicor_sync_status}
                        lastSyncAt={order.last_sync_success_at}
                        lastAttemptAt={order.last_sync_attempt_at}
                        errorMessage={order.sync_error_message}
                        externalRef={order.epicor_order_num}
                        entityLabel={`Order ${order.order_number}`}
                      />
                    </TableCell>

                    <TableCell>
                      {/* Plant cell — assign-button only on DRAFT
                          orders, read-only chip elsewhere. The grid
                          is the only place that exposes assignment;
                          there's no detail-page edit on purpose. */}
                      {/* Case-insensitive — OrderStatus.DRAFT.value
                          is "DRAFT" on the backend; we lower-case both
                          sides to make this robust against either form
                          (some endpoints lowercase status before
                          returning, others don't). */}
                      {String(order.status).toLowerCase() === 'draft' ? (
                        order.plant_code ? (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Chip
                              size="small"
                              label={order.plant_code}
                              color="primary"
                              variant="outlined"
                            />
                            <Button
                              size="small"
                              onClick={() => setPlantDialog({ open: true, order })}
                              sx={{ minWidth: 0, p: 0.5 }}
                            >
                              Change
                            </Button>
                          </Box>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => setPlantDialog({ open: true, order })}
                          >
                            Assign plant
                          </Button>
                        )
                      ) : order.plant_code ? (
                        <Chip
                          size="small"
                          label={order.plant_code}
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {order.item_count} items
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(order.total_amount)}
                        </Typography>
                        {order.total_discount_amount && order.total_discount_amount > 0 && (
                          <Typography variant="caption" color="success.main">
                            -{formatCurrency(order.total_discount_amount)} discount
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(order.order_date).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">
                        {order.delivery_date ? new Date(order.delivery_date).toLocaleDateString() : '-'}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Order Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewOrder(order)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>

                        {canResyncOrder(order) && (
                          <Tooltip
                            title={
                              order.epicor_order_num
                                ? 'Pull latest status from Epicor'
                                : 'Push this DRAFT order to Epicor'
                            }
                          >
                            {/* Extra span so the disabled IconButton
                                still shows its tooltip. */}
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleResyncWithEpicor(order)}
                                disabled={resyncingOrderId === order.id}
                                aria-label="sync with epicor"
                              >
                                {resyncingOrderId === order.id ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <SyncIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}

                        {/* Documents — consolidated into one icon
                            that opens the DMS Documents dialog.
                            Previously there were two icons: a green
                            Receipt for direct signed-invoice download
                            and a blue Folder for the dialog. The
                            direct-download flow silently 404'd when
                            Zegen hadn't posted the invoice yet and
                            gave operators no visibility into DN/FP
                            availability. Dialog handles the invoice
                            + supporting docs + status in one place.
                            Receipt icon retained since it reads as
                            "documents" better than the folder. */}
                        {isInvoiceAvailable(order.status) && (
                          <Tooltip title="Documents (Invoice / Surat Jalan / Faktur Pajak)">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => setDmsDialogOrder(order)}
                              aria-label="view documents"
                            >
                              <Receipt />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>

      {/* Order Detail Dialog */}
      <OrderDetailDialog
        order={selectedOrder}
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        onResync={handleResyncWithEpicor}
        resyncingOrderId={resyncingOrderId}
        canResyncOrder={canResyncOrder}
      />

      {plantDialog.order && (
        <PlantAssignDialog
          open={plantDialog.open}
          orderId={plantDialog.order.id}
          orderNumber={plantDialog.order.order_number}
          currentPlantCode={plantDialog.order.plant_code}
          onClose={() => setPlantDialog({ open: false, order: null })}
          onAssigned={(code) => {
            // Local optimistic update — keeps the chip in sync without
            // re-fetching the whole grid for one row change.
            setOrders((prev) =>
              prev.map((o) => (o.id === plantDialog.order!.id ? { ...o, plant_code: code } : o))
            );
          }}
        />
      )}

      {/* Resync outcome dialog. Success shows the moved fields as a
          list; empty changes[] means "already in sync". Failure path
          is surfaced separately as resyncError below. */}
      <Dialog
        open={resyncResult !== null}
        onClose={() => setResyncResult(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Sync with Epicor{resyncResult ? ` — ${resyncResult.order_number}` : ''}
        </DialogTitle>
        <DialogContent>
          {resyncResult && (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Direction:{' '}
                <strong>
                  {resyncResult.direction === 'status'
                    ? 'Epicor → Portal (pulled latest status)'
                    : 'Portal → Epicor (pushed order forward)'}
                </strong>
              </Typography>
              {resyncResult.changes.length === 0 ? (
                <Alert severity="info">
                  Order was already in sync — nothing to update.
                </Alert>
              ) : (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {resyncResult.changes.length} field
                    {resyncResult.changes.length === 1 ? '' : 's'} updated:
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      backgroundColor: 'grey.100',
                      p: 2,
                      borderRadius: 1,
                      fontSize: 13,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {resyncResult.changes.join('\n')}
                  </Box>
                </>
              )}
              {resyncResult.synced_at && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  Synced at {new Date(resyncResult.synced_at).toLocaleString()}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResyncResult(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Error dialog for 404/409/502/network -- kept as a Dialog for
          consistency with the success outcome above, rather than a
          Snackbar, so the operator has time to read what went wrong
          (esp. the 409 explanation for ambiguous order state). */}
      <Dialog
        open={resyncError !== null}
        onClose={() => setResyncError(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Sync with Epicor — failed</DialogTitle>
        <DialogContent>
          {resyncError && <Alert severity="error">{resyncError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResyncError(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* DMS Documents dialog for the currently-selected order.
          Opens from the folder icon on each row AND from the
          OrderDetailDialog "View DMS Documents" action. */}
      <BackofficeDmsDocumentsDialog
        orderId={dmsDialogOrder?.id ?? null}
        contextLabel={
          dmsDialogOrder
            ? `${dmsDialogOrder.order_number} — ${dmsDialogOrder.customer_name}`
            : undefined
        }
        open={dmsDialogOrder !== null}
        onClose={() => setDmsDialogOrder(null)}
      />
    </Box>
  );
};

export default OrderManagementPage;